<template>
    <div>
        <canvas ref="sectionChartCanvas"></canvas>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
Chart.register(...registerables);

export default {
    name: 'ConfectionProductionReportingSectionsGraphicData',

    props: {
        referencesData: {
            type: Object,
            required: true,
        },
        currentField: {
            type: Object,
            required: true,
        },
        forceLineChart: {
            type: Boolean,
            default: false,
        },
        forceBarChart: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            chart: null,
        };
    },
    computed: {
        fieldKey() {
            return this.currentField.key;
        },
        fieldLabel() {
            return this.currentField.label;
        },
        isTimeType() {
            return this.currentField.isTimeType || false;
        },
        suffix() {
            return this.currentField.suffix || '';
        },
        labels() {
            const set = new Set();
            Object.values(this.referencesData).forEach((line) => {
                Object.values(line.sectionsData).forEach((section) => {
                    set.add(section.sectionStartDate);
                });
            });
            return Array.from(set).sort((a, b) => new Date(a) - new Date(b));
        },
        datasets() {
            const lineCount = Object.keys(this.referencesData).length;
            if (lineCount === 0) return [];
            return Object.values(this.referencesData).map((line, index) => ({
                label: line.name,
                data: this.labels.map((label) => {
                    const section = Object.values(line.sectionsData).find(
                        (sec) => sec.sectionStartDate === label
                    );
                    const rawValue = section ? section[this.fieldKey] || 0 : 0;
                    const value = this.suffix === '%' ? Math.min(rawValue, 100) : rawValue;
                    return value;
                }),
                ...(lineCount > 7
                    ? {
                        borderColor: line.color,
                        backgroundColor: line.color.replace('rgb(', 'rgba(').replace(')', ', 0.5)'),
                    }
                    : {}
                ),
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                yAxisID: 'y-axis',
            }));
        },
        chartType() {
            if (this.forceLineChart) return 'line';
            if (this.forceBarChart) return 'bar';
            return this.defaultChartType;
        },
        defaultChartType() {
            return this.labels.length > 21 ? 'line' : 'bar';
        },
    },
    methods: {
        createChart() {
            const ctx = this.$refs.sectionChartCanvas.getContext('2d');
            if (this.chart) {
                this.chart.destroy();
            }
            const yAxisOptions = {
                beginAtZero: true,
                title: {
                    display: true,
                    text: this.fieldLabel,
                },
            };
            if (this.suffix === '%') {
                yAxisOptions.max = 100;
            }

            this.chart = new Chart(ctx, {
                type: this.chartType,
                data: {
                    labels: this.labels,
                    datasets: this.datasets,
                },
                options: {
                    responsive: true,
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (tooltipItem) => {
                                    const dataset = tooltipItem.dataset;
                                    const index = tooltipItem.dataIndex;
                                    const value = dataset.data[index];
                                    return `${dataset.label}: ${this.formatValue(value)}`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'time',
                            time: {
                                unit: 'day',
                                tooltipFormat: 'PP',
                                displayFormats: { day: 'yyyy-MM-dd' },
                            },
                            title: {
                                display: true,
                                text: 'Fecha de sección',
                            },
                        },
                        'y-axis': yAxisOptions,
                    },
                },
            });
        },
        formatValue(value) {
            let returnValue = value;
            if (this.isTimeType) {
                returnValue = this.formatSecondsToTime(value);
            }
            if (typeof returnValue === 'number') {
                returnValue = new Intl.NumberFormat('es-ES', { useGrouping: true }).format(returnValue);
            }
            if (this.suffix === '%') {
                const numeric = parseFloat(returnValue);
                if (!isNaN(numeric)) {
                    returnValue = Math.min(numeric, 100);
                }
            }
            return `${returnValue}${this.suffix}`;
        },
        formatSecondsToTime(seconds) {
            if (isNaN(seconds) || seconds < 0) return '0s';

            const units = [
                { label: 'a', value: 31556926 },
                { label: 'mo', value: 2629743 },
                { label: 'w', value: 604800 },
                { label: 'd', value: 86400 },
                { label: 'h', value: 3600 },
                { label: 'm', value: 60 },
                { label: 's', value: 1 },
            ];

            let remaining = Math.floor(seconds);
            const parts = [];

            for (const unit of units) {
                const count = Math.floor(remaining / unit.value);
                if (count > 0 || (unit.label === 's' && parts.length === 0)) {
                    parts.push(`${count}${unit.label}`);
                    remaining -= count * unit.value;
                }
            }

            return parts.join(' ');
        },
    },
    watch: {
        referencesData: {
            deep: true,
            handler() {
                this.createChart();
            },
        },
        currentField: {
            deep: true,
            handler() {
                this.createChart();
            },
        },
        forceLineChart() {
            if (this.forceBarChart == false) {
                this.createChart();
            }
        },
        forceBarChart() {
            if (this.forceLineChart == false) {
                this.createChart();
            }
        },
    },
    mounted() {
        this.createChart();
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
    },
};
</script>